import React from "react";
import { Breakdown } from "../../../types/outbox.types";
import { LaneBadge } from "./LaneBadge";

const FAIL_RATE_WARNING_THRESHOLD = 0.05;

interface EventTypeBreakdownProps {
  breakdown: Breakdown;
}

export const EventTypeBreakdown: React.FC<EventTypeBreakdownProps> = ({ breakdown }) => {
  if (!breakdown.items.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Breakdown per event type
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla ostatnich 24h
        </div>
      </div>
    );
  }

  const hasLane = breakdown.items.some((item) => item.lane !== undefined);
  const hasAvgQueueWaitMs = breakdown.items.some((item) => item.avgQueueWaitMs !== undefined);
  const hasAvgProcessingMs = breakdown.items.some((item) => item.avgProcessingMs !== undefined);

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="outbox-event-breakdown"
    >
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Breakdown per event type (24h)
      </h3>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Event Type
              </th>
              {hasLane && (
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Lane
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Count
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Avg Duration
              </th>
              {hasAvgQueueWaitMs && (
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Queue Wait
                </th>
              )}
              {hasAvgProcessingMs && (
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Processing
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                P95 Duration
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Fail Rate
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {breakdown.items.map((item) => (
              <tr key={item.eventType} className="hover:bg-neutral-50">
                <td className="px-4 py-3 text-sm text-neutral-900 font-mono">
                  {item.eventType}
                </td>
                {hasLane && (
                  <td className="px-4 py-3 text-sm">
                    {item.lane ? (
                      <LaneBadge lane={item.lane} />
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                  {item.count.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                  {item.avgDurationMs.toFixed(0)}ms
                </td>
                {hasAvgQueueWaitMs && (
                  <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                    {item.avgQueueWaitMs !== undefined ? `${item.avgQueueWaitMs.toFixed(0)}ms` : "-"}
                  </td>
                )}
                {hasAvgProcessingMs && (
                  <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                    {item.avgProcessingMs !== undefined ? `${item.avgProcessingMs.toFixed(0)}ms` : "-"}
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                  {item.p95DurationMs.toFixed(0)}ms
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span
                    className={`${
                      item.failRate > FAIL_RATE_WARNING_THRESHOLD
                        ? "text-red-600 font-medium"
                        : "text-neutral-900"
                    }`}
                  >
                    {(item.failRate * 100).toFixed(2)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventTypeBreakdown;
